/**
 * Streaming waveform peak builder (pure; no DOM).
 *
 * Audio arrives as chunks of planar float samples with a start timestamp. Each chunk's samples are
 * bucketed into `rate` buckets per second, keeping the absolute peak across all channels. The
 * result matches the server's former waveform format: `{ rate, peaks }` with peaks 0..255, where
 * 255 is full scale.
 */
import type { Waveform } from '@ve/core';

export const WAVEFORM_RATE = 100;

export class WaveformBuilder {
  private buckets = new Float32Array(1024);
  private length = 0;

  constructor(private readonly rate = WAVEFORM_RATE) {}

  /**
   * Adds a chunk of audio.
   * @param timestamp Start time of the chunk in seconds (may be negative for priming; clamped).
   * @param sampleRate Samples per second of the chunk.
   * @param planes One Float32Array per channel, all of the same length.
   */
  add(timestamp: number, sampleRate: number, planes: ReadonlyArray<Float32Array>): void {
    const frames = planes[0]?.length ?? 0;
    if (frames === 0 || sampleRate <= 0) return;
    const bucketsPerSample = this.rate / sampleRate;
    const startBucket = timestamp * this.rate;
    for (let i = 0; i < frames; i++) {
      const b = Math.floor(startBucket + i * bucketsPerSample);
      if (b < 0) continue;
      let peak = 0;
      for (const plane of planes) {
        const v = Math.abs(plane[i]!);
        if (v > peak) peak = v;
      }
      if (b >= this.length) this.grow(b + 1);
      if (peak > this.buckets[b]!) this.buckets[b] = peak;
    }
  }

  /**
   * Finishes the waveform. When `duration` is given, the result has exactly
   * `ceil(duration * rate)` buckets (silent tail padded with zeros, overshoot trimmed).
   */
  finish(duration?: number): Waveform {
    const count = duration !== undefined ? Math.max(0, Math.ceil(duration * this.rate)) : this.length;
    const peaks = new Array<number>(count);
    for (let i = 0; i < count; i++) {
      const v = i < this.length ? this.buckets[i]! : 0;
      peaks[i] = Math.min(255, Math.max(0, Math.round(v * 255)));
    }
    return { rate: this.rate, peaks };
  }

  private grow(minLength: number): void {
    if (minLength > this.buckets.length) {
      let size = this.buckets.length;
      while (size < minLength) size *= 2;
      const next = new Float32Array(size);
      next.set(this.buckets.subarray(0, this.length));
      this.buckets = next;
    }
    this.length = Math.max(this.length, minLength);
  }
}
